import { motion } from "framer-motion";
import { scrollToSection } from "../../lib/scroll";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7 },
};

const CTA = ({ children = "Request a confidential briefing", testid, solid = false, target = "briefing" }) => (
  <button
    type="button"
    onClick={() => scrollToSection(target)}
    data-testid={testid}
    className={
      solid
        ? "inline-block font-mono text-sm uppercase tracking-[0.2em] px-10 py-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300"
        : "font-mono text-[11px] uppercase tracking-[0.2em] text-ochre hover:text-ember transition-colors duration-300"
    }
  >
    {children}{solid ? "" : " →"}
  </button>
);

const packages = [
  {
    title: "Governance Diagnostic",
    suitable: "Organisations requiring an initial assessment of a policy, program, service, framework or institutional response.",
    description: "Bigibila undertakes a bounded review to identify material governance concerns, accountability gaps and areas requiring deeper investigation.",
    outputs: ["Executive diagnostic", "Priority-risk summary", "Governance observations", "Recommended next actions", "Decision-maker briefing"],
    engagement: "Focused and time-limited",
    price: "Scoped by enquiry",
    button: "Discuss a Governance Diagnostic",
  },
  {
    title: "Governance Gap Analysis",
    suitable: "Organisations requiring a deeper review across legislation, policy, institutional responsibilities, evidence and operational practice.",
    description: "Bigibila identifies where stated commitments, decision-making authority and institutional practice fail to align, including consequences for First Nations peoples and communities.",
    outputs: ["Executive governance report", "Evidence and accountability map", "Material gap findings", "Cultural and institutional risk analysis", "Prioritised recommendations", "Implementation roadmap", "Executive presentation"],
    price: "Custom institutional engagement",
    button: "Request a Gap Analysis briefing",
  },
  {
    title: "Indigenous Data Sovereignty Review",
    suitable: "Data platforms, research programs, service systems, digital products and organisations handling First Nations information.",
    description: "Bigibila examines whether authority, permission, control, benefit and accountability are embedded in the governance of First Nations data.",
    outputs: ["Data-governance findings", "Authority and permission assessment", "Cultural-risk findings", "Data-control recommendations", "Governance requirements", "Implementation priorities"],
    price: "Scoped to system and data environment",
    button: "Discuss a Data Sovereignty Review",
  },
  {
    title: "Cultural Governance Assurance",
    suitable: "Organisations that need continuing oversight while designing or implementing a policy, service, system, partnership or reform.",
    description: "Bigibila provides independent First Nations-led assurance focused on cultural authority, institutional accountability and whether agreed protections remain operational throughout delivery.",
    outputs: ["Assurance plan", "Periodic governance reviews", "Executive assurance reports", "Escalation notices", "Corrective-action recommendations", "Final assurance statement"],
    price: "Project or retainer engagement",
    button: "Discuss Cultural Governance Assurance",
  },
  {
    title: "Lore-Led Architecture Advisory",
    suitable: "First Nations-controlled initiatives, governance models, digital infrastructure and community-led systems.",
    description: "Bigibila supports the design of governance environments grounded in First Nations authority, continuity, responsibility and locally controlled decision-making.",
    outputs: ["Governance design brief", "Authority and responsibility model", "Decision-right recommendations", "Cultural protection requirements", "Operating-model recommendations", "Implementation support"],
    price: "Confidential scoping required",
    button: "Request a confidential discussion",
  },
  {
    title: "Institutional Partnership",
    suitable: "Universities, governments, funders and organisations seeking sustained access to Bigibila's advisory capability.",
    description: "A structured partnership providing recurring First Nations-led governance review, strategic advice, executive briefings and approved use of specified Bigibila outputs.",
    outputs: ["Scheduled executive briefings", "Project reviews", "Governance assurance", "Research or policy analysis", "Licensed organisational outputs", "Implementation monitoring"],
    price: "Annual or multi-stage agreement",
    button: "Explore an Institutional Partnership",
  },
];

const clientReceives = ["Approved final outputs", "Defined organisational usage rights", "Implementation recommendations", "Agreed presentation or reporting rights", "Ongoing support where contracted"];
const bigibilaRetains = ["Codebook", "Methodology", "Decision rules", "Scoring architecture", "Analytical instruments", "Internal working papers", "System logic", "Cultural and intellectual property ownership"];

const scopeFactors = [
  "Size and complexity of the system",
  "Number of documents and evidence sources",
  "Number of institutions or jurisdictions",
  "Sensitivity of the material",
  "Cultural authority and permissions required",
  "Stakeholder and decision-maker requirements",
  "Reporting requirements",
  "Timeframe",
  "Implementation support",
  "Ongoing assurance needs",
];

const valuePoints = [
  "First Nations-led and culturally governed",
  "Designed for complex institutional environments",
  "Evidence-based and methodologically tested",
  "Focused on operational accountability",
  "Suitable for policy, programs, services and digital systems",
  "Clear executive outputs",
  "Protected Indigenous intellectual property",
  "Independent governance insight",
];

const procurement = [
  ["ABN", "Available on request"],
  ["Supply Nation status", "Registration details available on request"],
  ["Insurance", "Details available on request"],
  ["Capability statement", "Available after enquiry"],
  ["Conflict and confidentiality", "Assessment undertaken before engagement"],
  ["Purchase orders and contracts", "Standard institutional requirements supported"],
];

export default function ServicesSection() {
  return (
    <>
      <section id="services" className="py-28 md:py-36 border-b border-line bg-surface1">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">Services</motion.p>
          <motion.h2 {...fadeUp} data-testid="services-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-4xl mb-8">
            First Nations-led <em className="text-ember">governance intelligence</em>
          </motion.h2>
          <motion.p {...fadeUp} className="text-stone2 leading-relaxed max-w-3xl text-lg mb-10">
            Bigibila helps First Nations organisations, governments, universities, service providers and institutional partners identify governance gaps, cultural risks and accountability failures—and establish practical pathways for change.
          </motion.p>
          <motion.div {...fadeUp}>
            <CTA solid testid="services-hero-cta" />
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-px bg-line/40 border border-line/40">
            {packages.map((p, i) => (
              <motion.article key={p.title} {...fadeUp} transition={{ duration: 0.6, delay: (i % 2) * 0.08 }} data-testid={`service-package-${i}`} className="bg-ink p-8 md:p-10 hover:bg-surface2 transition-colors duration-300 flex flex-col">
                <span className="font-mono text-xs text-ember block mb-4">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl md:text-3xl mb-4">{p.title}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">Suitable for</p>
                <p className="text-sm text-stone2 leading-relaxed mb-5">{p.suitable}</p>
                <p className="text-sm text-bone leading-relaxed mb-6">{p.description}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-3">Potential outputs</p>
                <ul className="space-y-1.5 mb-6">
                  {p.outputs.map((o) => (
                    <li key={o} className="text-sm text-stone2 flex gap-3"><span className="text-ember">—</span>{o}</li>
                  ))}
                </ul>
                <div className="mt-auto pt-4 border-t border-line/60 flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    {p.engagement && <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone2 mb-1">Indicative engagement: {p.engagement}</p>}
                    <span className="font-mono text-sm text-ember">{p.price}</span>
                  </div>
                  <CTA testid={`service-package-btn-${i}`}>{p.button}</CTA>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="workshop-section" className="py-24 md:py-32 border-b border-line bg-surface1">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">Workshop</motion.p>
            <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl tracking-tight mb-8">
              Facilitated Governance <em className="text-ember">Workshop</em>
            </motion.h2>
            <motion.p {...fadeUp} className="text-stone2 leading-relaxed mb-6">
              A Bigibila-led session examining governance, authority, accountability, cultural risk and Indigenous Data Sovereignty within the participating organisation's context.
            </motion.p>
            <motion.p {...fadeUp} data-testid="workshop-clarification" className="text-sm text-bone leading-relaxed border-l-2 border-ember pl-5 mb-8">
              The workshop provides insight and facilitated application. It does not teach, disclose or transfer Bigibila's codebook, scoring architecture, decision rules or internal methodology.
            </motion.p>
            <motion.div {...fadeUp}>
              <CTA solid testid="workshop-cta" target="book">Book a workshop</CTA>
            </motion.div>
          </div>
          <motion.div {...fadeUp} className="border border-line bg-ink p-8 md:p-10 self-start">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-4">Potential outputs</p>
            <ul className="space-y-3">
              {["Facilitated session", "Executive observations", "Priority-action summary", "Optional follow-up briefing"].map((o) => (
                <li key={o} className="text-sm text-stone2 flex gap-3"><span className="text-ember">—</span>{o}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section data-testid="licensing-section" className="py-24 md:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl tracking-tight mb-8 max-w-3xl">
            What an output licence <em className="text-ember">means</em>
          </motion.h2>
          <motion.p {...fadeUp} data-testid="licensing-body" className="text-stone2 leading-relaxed max-w-3xl mb-4">
            Where agreed, clients receive a limited licence to use specified Bigibila reports, findings, dashboards or implementation outputs for the approved organisational purpose.
          </motion.p>
          <motion.p {...fadeUp} className="text-bone leading-relaxed max-w-3xl mb-12 border-l-2 border-ember pl-5">
            An output licence does not provide ownership of, or access to, the methodology used to create the output.
          </motion.p>
          <motion.div {...fadeUp} data-testid="licence-comparison-table" className="grid md:grid-cols-2 gap-px bg-line/40 border border-line/40 max-w-5xl">
            <div className="bg-ink p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ochre mb-6">Client receives</p>
              <ul className="space-y-3">
                {clientReceives.map((x) => <li key={x} className="text-sm text-stone2 flex gap-3"><span className="text-ember">✓</span>{x}</li>)}
              </ul>
            </div>
            <div className="bg-surface1 p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember mb-6">Bigibila retains</p>
              <ul className="space-y-3">
                {bigibilaRetains.map((x) => <li key={x} className="text-sm text-stone2 flex gap-3"><span className="text-ember">×</span>{x}</li>)}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="scope-section" className="py-24 md:py-32 border-b border-line bg-surface1">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl tracking-tight mb-12 max-w-3xl">
            How engagements are <em className="text-ember">scoped</em>
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-line/40 border border-line/40">
            {scopeFactors.map((f, i) => (
              <motion.div key={f} {...fadeUp} transition={{ duration: 0.5, delay: (i % 5) * 0.05 }} className="bg-ink p-6 hover:bg-surface2 transition-colors duration-300">
                <span className="font-mono text-xs text-ember block mb-3">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm text-stone2 leading-relaxed">{f}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="value-section" className="py-24 md:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl tracking-tight mb-12 max-w-3xl">
            Why organisations engage <em className="text-ember">Bigibila</em>
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-5 mb-12 max-w-6xl">
            {valuePoints.map((v) => (
              <motion.p key={v} {...fadeUp} className="text-sm text-stone2 leading-relaxed flex gap-3"><span className="text-ember">—</span>{v}</motion.p>
            ))}
          </div>
          <motion.p {...fadeUp} data-testid="reliability-statement" className="text-bone leading-relaxed max-w-3xl border border-ember/40 bg-surface1 p-6 md:p-8">
            Bigibila Governance Archaeology has achieved a Cohen's kappa result of 0.952 through prospective independent reliability testing. Detailed testing instruments and methodology records remain confidential.
          </motion.p>
        </div>
      </section>

      <section data-testid="procurement-section" className="py-24 md:py-32 border-b border-line bg-surface1">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl tracking-tight mb-8 max-w-3xl">
            Procurement and <em className="text-ember">engagement</em>
          </motion.h2>
          <motion.p {...fadeUp} className="text-stone2 leading-relaxed max-w-3xl mb-12">
            Bigibila can support direct engagements, scoped projects, pilot initiatives, research partnerships, subcontracting arrangements and longer-term institutional assurance.
          </motion.p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line/40 border border-line/40 max-w-5xl">
            {procurement.map(([label, value]) => (
              <motion.div key={label} {...fadeUp} className="bg-ink p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">{label}</p>
                <p className="text-sm text-stone2">{value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-b border-line">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <motion.h2 {...fadeUp} data-testid="final-cta-heading" className="font-serif text-3xl md:text-5xl tracking-tight mb-8">
            Bring Bigibila in <em className="text-ember">before the system is locked in</em>
          </motion.h2>
          <motion.p {...fadeUp} className="text-stone2 leading-relaxed max-w-2xl mx-auto mb-10">
            Early First Nations-led governance analysis can identify authority, accountability and cultural-risk issues before they become embedded in policy, technology, contracting or service delivery.
          </motion.p>
          <motion.div {...fadeUp}>
            <CTA solid testid="services-final-cta" />
          </motion.div>
        </div>
      </section>
    </>
  );
}
